import { Badge, Button, Tooltip } from '@/components/ui';
import { PlayIcon, ClockIcon, ArrowsPointingInIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import type { Region } from '@/types/api';

interface Phase {
  phase_id?: string;
  label: string;
  phase_type?: string;
  start_frame: number;
  end_frame: number;
  importance?: string;
  description?: string;
  biomechanical_markers?: string[];
  timing_goal?: string;
}

interface PhaseDetailsPanelProps {
  phase: Phase | null;
  fps?: number;
  regions?: Region[];
  phaseRegions?: Region[]; // Regions relevant to this specific phase
  onPlayPhase?: (startFrame: number, endFrame: number) => void;
  onClose?: () => void;
  className?: string;
}

// Importance badge colors
const IMPORTANCE_COLORS: Record<string, { badge: 'success' | 'warning' | 'error' | 'secondary'; label: string }> = {
  critical: { badge: 'error', label: 'Critical' },
  high: { badge: 'warning', label: 'High' },
  medium: { badge: 'secondary', label: 'Medium' },
  low: { badge: 'secondary', label: 'Low' },
};

// Phase type display names
const PHASE_TYPE_LABELS: Record<string, string> = {
  preparation: 'Preparation',
  transition: 'Transition',
  execution: 'Execution',
  recovery: 'Recovery',
  hold: 'Hold',
  dynamic: 'Dynamic',
};

function PhaseDetailsPanel({
  phase,
  fps = 30,
  regions = [],
  phaseRegions,
  onPlayPhase,
  onClose,
  className,
}: PhaseDetailsPanelProps) {
  if (!phase) {
    return (
      <div className={cn('p-6 text-center text-text-muted bg-dark-elevated-1 rounded-lg', className)}>
        <ArrowsPointingInIcon className="h-8 w-8 mx-auto mb-2 text-text-muted" />
        <p>Click on a phase in the timeline to see details</p>
      </div>
    );
  }

  const durationFrames = phase.end_frame - phase.start_frame;
  const durationSeconds = durationFrames / fps;
  const importanceInfo = IMPORTANCE_COLORS[phase.importance || 'medium'] || IMPORTANCE_COLORS.medium;
  const phaseTypeLabel = PHASE_TYPE_LABELS[phase.phase_type || ''] || phase.phase_type || 'Unknown';

  // Use phase-specific regions if provided, otherwise show all
  const displayRegions = phaseRegions || regions;

  return (
    <div className={cn('bg-dark-card border border-border-subtle rounded-lg overflow-hidden', className)}>
      {/* Header */}
      <div className="bg-dark-elevated-1 px-4 py-3 border-b border-border-subtle">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-lg text-text-primary">{phase.label}</h3>
            <Badge variant={importanceInfo.badge}>
              {importanceInfo.label}
            </Badge>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-text-muted hover:text-text-primary transition-colors"
            >
              ×
            </button>
          )}
        </div>
        {phase.phase_type && (
          <p className="text-sm text-text-secondary mt-1">Type: {phaseTypeLabel}</p>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Frame and Time Info */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-dark-elevated-1 rounded-lg p-3">
            <div className="flex items-center gap-2 text-text-secondary mb-1">
              <ClockIcon className="h-4 w-4" />
              <span className="text-xs font-medium">Duration</span>
            </div>
            <p className="text-lg font-semibold text-text-primary">{durationSeconds.toFixed(2)}s</p>
            <p className="text-xs text-text-muted">{durationFrames} frames</p>
          </div>
          <div className="bg-dark-elevated-1 rounded-lg p-3">
            <div className="text-xs font-medium text-text-secondary mb-1">Frame Range</div>
            <p className="text-lg font-semibold text-text-primary">
              {phase.start_frame} - {phase.end_frame}
            </p>
            <p className="text-xs text-text-muted">
              {((phase.start_frame / fps)).toFixed(2)}s - {((phase.end_frame / fps)).toFixed(2)}s
            </p>
          </div>
        </div>

        {/* Play Phase Button */}
        {onPlayPhase && (
          <Button
            onClick={() => onPlayPhase(phase.start_frame, phase.end_frame)}
            className="w-full"
            variant="primary"
          >
            <PlayIcon className="h-5 w-5 mr-2" />
            Play This Phase
          </Button>
        )}

        {/* Description */}
        {phase.description && (
          <div>
            <h4 className="text-sm font-medium text-text-primary mb-1">Description</h4>
            <p className="text-sm text-text-secondary">{phase.description}</p>
          </div>
        )}

        {/* Timing Goal */}
        {phase.timing_goal && (
          <div>
            <h4 className="text-sm font-medium text-text-primary mb-1">Timing Goal</h4>
            <Badge variant="secondary">{phase.timing_goal}</Badge>
          </div>
        )}

        {/* Biomechanical Markers */}
        {phase.biomechanical_markers && phase.biomechanical_markers.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-text-primary mb-2">Key Markers</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-text-secondary">
              {phase.biomechanical_markers.map((marker, idx) => (
                <li key={idx}>{marker}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Relevant Regions */}
        {displayRegions.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-text-primary mb-2">
              {phaseRegions ? 'Phase-Specific Regions' : 'Tracked Regions'}
            </h4>
            <div className="space-y-2">
              {displayRegions.map((region, idx) => (
                <div
                  key={region.id || idx}
                  className="p-2 bg-dark-elevated-1 rounded border border-border-subtle"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-sm font-medium text-text-primary">{region.name}</span>
                      <span className="text-xs text-text-muted ml-2">
                        ({region.landmark_indices?.length || 0} landmarks)
                      </span>
                    </div>
                    <Badge variant="secondary" size="sm">
                      {region.type}
                    </Badge>
                  </div>

                  {/* Goal badges */}
                  {(region.spatial_goal?.enabled || region.movement_goal?.enabled) && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {region.spatial_goal?.enabled && (
                        <Tooltip
                          content={
                            <div className="text-xs space-y-1">
                              <p><strong>Spatial Goal</strong></p>
                              <p>Source: {region.spatial_goal.source_landmark}</p>
                              <p>Target: {region.spatial_goal.target.type === 'landmark'
                                ? region.spatial_goal.target.target_landmark
                                : 'Template Anchor'}</p>
                              {region.spatial_goal.objective && (
                                <>
                                  <p>Mode: {region.spatial_goal.objective.mode}</p>
                                  {region.spatial_goal.objective.tolerance && (
                                    <p>Tolerance: {region.spatial_goal.objective.tolerance.value}{region.spatial_goal.objective.tolerance.kind === 'percent' ? '%' : 'px'}</p>
                                  )}
                                </>
                              )}
                            </div>
                          }
                        >
                          <Badge variant="info" size="sm" className="cursor-help">
                            Distance Check
                          </Badge>
                        </Tooltip>
                      )}
                      {region.movement_goal?.enabled && (
                        <Tooltip
                          content={
                            <div className="text-xs space-y-1">
                              <p><strong>Movement Goal</strong></p>
                              <p>Mode: {region.movement_goal.mode}</p>
                              <p>Reference: {region.movement_goal.reference_type}</p>
                              <p>Aggregation: {region.movement_goal.aggregation}</p>
                              {region.movement_goal.direction && (
                                <p>Direction: {region.movement_goal.direction.direction}</p>
                              )}
                            </div>
                          }
                        >
                          <Badge variant="info" size="sm" className="cursor-help">
                            Movement Goal
                          </Badge>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No regions message */}
        {displayRegions.length === 0 && (
          <div className="text-sm text-text-muted text-center py-2">
            No body regions defined for this phase
          </div>
        )}
      </div>
    </div>
  );
}

export default PhaseDetailsPanel;
